import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// apicore
import { APICore, setAuthorization } from "../../helpers/api/apiCore";

// helpers
import {
  login as loginApi,
  logout as logoutApi,
  forgotPassword as forgotPasswordApi,
} from "../../helpers/";

// analytics — login/logout are emitted here rather than from a route effect,
// because both logout paths end in a full document load (ANALYTICS.md B3).
import { setUser, clearUser, track } from "../../analytics";
import { EVENTS } from "../../analytics/events";

// actions
import { authApiResponseSuccess, authApiResponseError } from "./actions";
import { disconnectSocket } from "../../socket";
import { queryClient } from "../../App";

// constants
import { AuthActionTypes } from "./constants";

interface UserData {
  payload: {
    username: string;
    password: string;
    fullname: string;
    email: string;
  };
  type: string;
}

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({
  payload: { email, password },
}: UserData): SagaIterator {
  try {
    const response = yield call(loginApi, { email, password });
    const user = response.data?.data;
    // NOTE - You can change this according to response format from your api
    api.setLoggedInUser(user);
    setAuthorization(user["access_token"]);
    // Identify first so the login event itself carries the user dimensions.
    setUser(user);
    track(EVENTS.LOGIN, { method: "password" });
    yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, user));
  } catch (error: any) {
    // A rate-limit refusal arrives as an AuthLimitError; the store keeps strings only.
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, error instanceof Error ? error.message : error));
    api.setLoggedInUser(null);
    setAuthorization(null);
  }
}

/**
 * Logout the user
 */
function* logout(): SagaIterator {
  // Before the API call and before the caller's redirect: the event needs the user
  // dimensions that clearUser() is about to drop, and a failing logoutApi must not
  // cost us the event — the local session is cleared either way.
  track(EVENTS.LOGOUT, { reason: "user" });
  clearUser();
  try {
    yield call(logoutApi);
    api.setLoggedInUser(null);
    setAuthorization(null);
    // Disconnect real-time channel and clear notifications cache
    try {
      disconnectSocket();
    } catch {}
    try {
      queryClient.removeQueries(['notifications']);
    } catch {}
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.LOGOUT_USER, error));
  }
}

function* forgotPassword({ payload: { email } }: UserData): SagaIterator {
  try {
    const response = yield call(forgotPasswordApi, { email });
    yield put(
      authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response.data)
    );
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, error));
  }
}
export function* watchLoginUser() {
  yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

export function* watchLogout() {
  yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}


export function* watchForgotPassword(): any {
  yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogout),
    fork(watchForgotPassword),
  ]);
}

export default authSaga;
